import React, { lazy, Suspense, useState } from 'react'
import Typography from '../common/Typography'
import { BetterToast } from '~/utils/toast'
import DocMarkdown from './DocMarkdown'
import { CopyIcon, CheckIcon } from '~/components/icons'
import { DownloadProgressButton } from '~/components/common/dropdowns/DownloadProgressButton'

// Markdown documents (Documentation Agent output, etc.) get a multi-format
// dropdown — md / pdf / docx — driven by the same client-side conversion
// pipeline DocsPanel uses for the Migration Accelerator. The dropdown
// eagerly imports `docx` + `jspdf` + `html2canvas` + the full `unified`
// markdown pipeline (several MB of JS), so it's behind `React.lazy` to keep
// it off the synchronous import graph for the much-more-common non-md
// download path. (CUR-59)
const LazyDownloadDocumentDropdown = lazy(() =>
  import('~/components/common/dropdowns/DownloadDocumentDropdown').then(m => ({
    default: m.DownloadDocumentDropdown
  }))
)
const DownloadDropdownFallback = <div className='h-9 w-[140px] shrink-0' aria-hidden='true' />

function yieldToProgressPaint(): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, 0))
}

export interface DocumentItem {
  name: string
  path: string
  content: string
  type: 'json' | 'md' | 'txt' | 'xml' | 'yaml' | 'csv'
}

export interface DocumentViewerProps {
  isOpen: boolean
  onClose: () => void
  document: DocumentItem
  currentIndex: number
  totalDocuments: number
  onNavigate: (direction: 'prev' | 'next') => void
}

export function DocumentViewer({
  isOpen,
  onClose,
  document,
  currentIndex,
  totalDocuments,
  onNavigate
}: DocumentViewerProps) {
  const [isCopying, setIsCopying] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)

  if (!isOpen) return null

  const handleDownload = async () => {
    setIsDownloading(true)
    await yieldToProgressPaint()

    try {
      const mimeTypeMap = {
        json: 'application/json',
        xml: 'application/xml',
        yaml: 'application/yaml',
        csv: 'text/csv',
        md: 'text/markdown',
        txt: 'text/plain'
      }

      const content = formatContent()
      const blob = new Blob([content], {
        type: mimeTypeMap[document.type] || 'text/plain'
      })
      const url = window.URL.createObjectURL(blob)
      const a = window.document.createElement('a')
      a.href = url
      a.download = document.name
      window.document.body.appendChild(a)
      a.click()
      window.document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
      BetterToast.success(`Downloaded ${document.name}`)
    } catch (error) {
      console.error('Download error:', error)
      BetterToast.error('Failed to download file')
    } finally {
      setIsDownloading(false)
    }
  }

  const handleCopy = async () => {
    setIsCopying(true)
    try {
      const content = formatContent()
      await navigator.clipboard.writeText(content)
      BetterToast.success('Content copied to clipboard')
    } catch (error) {
      console.error('Copy error:', error)
      BetterToast.error('Failed to copy content')
    } finally {
      setIsCopying(false)
    }
  }

  const formatContent = () => {
    switch (document.type) {
      case 'json':
        try {
          return JSON.stringify(JSON.parse(document.content), null, 2)
        } catch {
          return document.content
        }
      case 'xml':
        // Basic XML formatting - in a real app you might use a proper XML formatter
        try {
          // Simple XML indentation
          return document.content.replace(/></g, '>\n<').replace(/^\s*\n/gm, '')
        } catch {
          return document.content
        }
      default:
        return document.content
    }
  }

  const getLanguageClass = () => {
    const languageMap = {
      json: 'language-json',
      xml: 'language-xml',
      yaml: 'language-yaml',
      csv: 'language-csv',
      md: 'language-markdown',
      txt: 'language-text'
    }
    return languageMap[document.type] || 'language-text'
  }

  const getFileTypeLabel = () => {
    const typeLabels = {
      json: 'JSON Document',
      xml: 'XML Document',
      yaml: 'YAML Document',
      csv: 'CSV Document',
      md: 'Markdown Document',
      txt: 'Text Document'
    }
    return typeLabels[document.type] || 'Document'
  }

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center'
      role='dialog'
      aria-modal='true'
    >
      {/* Backdrop */}
      <div className='absolute inset-0 bg-black/45' onClick={onClose} />

      {/* Modal */}
      <div className='relative bg-white rounded-2xl shadow-2xl max-w-4xl w-[90vw] max-h-[90vh] flex flex-col'>
        {/* Header */}
        <div className='flex items-center justify-between px-6 py-4 border-b border-gray-200'>
          <div className='flex flex-col'>
            <Typography
              fontFamily='geist'
              fontSize='titleMd'
              fontWeight='semibold'
              className='text-black/88'
            >
              {document.name}
            </Typography>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='normal'
              className='text-black/65'
            >
              {getFileTypeLabel()}
            </Typography>
          </div>

          <div className='flex items-center gap-3'>
            {document.type === 'md' ? (
              <Suspense fallback={DownloadDropdownFallback}>
                <LazyDownloadDocumentDropdown
                  content={document.content}
                  filename={document.name.replace(/\.md$/i, '')}
                />
              </Suspense>
            ) : (
              <DownloadProgressButton
                onClick={handleDownload}
                disabled={isDownloading}
                isLoading={isDownloading}
                className='h-9 flex p-[8px_16px] justify-center items-center gap-2 text-base rounded-[12px] border border-gray-300 bg-black text-white hover:bg-gray-800 active:bg-gray-700 disabled:cursor-wait'
                aria-label={`Download ${document.name}`}
              >
                <span className='flex items-center gap-2'>
                  <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
                    <path d='M8 10.5L5 7.5H7V2H9V7.5H11L8 10.5Z' fill='currentColor' />
                    <path
                      d='M2 12V13C2 13.5523 2.44772 14 3 14H13C13.5523 14 14 13.5523 14 13V12'
                      stroke='currentColor'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                    />
                  </svg>
                  <span className='font-normal'>Download</span>
                </span>
              </DownloadProgressButton>
            )}

            {/* Close Button */}
            <button
              onClick={onClose}
              className='p-2 rounded-lg hover:bg-gray-100 transition-colors'
              aria-label='Close document viewer'
            >
              <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
                <path
                  d='M15 5L5 15M5 5L15 15'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  className='text-black/45'
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className='flex-1 overflow-hidden'>
          <div className='h-full overflow-auto p-6'>
            <div className='bg-gray-50 rounded-lg p-4 relative'>
              {/* Copy Button - Top Right Corner */}
              <button
                onClick={handleCopy}
                disabled={isCopying}
                className='absolute right-4 top-4 flex gap-1.5 items-center rounded-md bg-white border border-gray-300 p-2 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed z-10'
                aria-label={`Copy ${document.name} content`}
              >
                {isCopying ? <CheckIcon className='w-4 h-4' /> : <CopyIcon className='w-4 h-4' />}
              </button>

              <div className='overflow-auto max-h-[60vh] pr-16'>
                {document.type === 'md' ? (
                  /* Render markdown files with DocMarkdown */
                  <div className='prose prose-sm max-w-none'>
                    <DocMarkdown
                      text={document.content}
                      fontSize='sm'
                      className='!pl-0 !text-black/88'
                    />
                  </div>
                ) : (
                  /* Render other file types as code */
                  <pre className={`whitespace-pre-wrap break-words ${getLanguageClass()}`}>
                    <code className='text-sm font-mono text-black/88 leading-relaxed block'>
                      {formatContent()}
                    </code>
                  </pre>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Navigation */}
        {totalDocuments > 1 && (
          <div className='flex items-center justify-center px-6 py-4 border-t border-gray-200'>
            <div className='flex items-center gap-4'>
              <button
                onClick={() => onNavigate('prev')}
                disabled={currentIndex === 0}
                className='flex items-center justify-center p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                aria-label='Previous document'
              >
                <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
                  <path
                    d='M12 16L6 10L12 4'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    className='text-black/65'
                  />
                </svg>
              </button>

              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                fontWeight='medium'
                className='text-black/65 px-4'
              >
                {currentIndex + 1} of {totalDocuments}
              </Typography>

              <button
                onClick={() => onNavigate('next')}
                disabled={currentIndex === totalDocuments - 1}
                className='flex items-center justify-center p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                aria-label='Next document'
              >
                <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
                  <path
                    d='M8 4L14 10L8 16'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    className='text-black/65'
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
